/**
 * Cookie-based storage utilities for unauthenticated users
 * 
 * Provides functions to store/retrieve threads from browser cookies
 * when users are not authenticated with Cognito.
 */

import type { SupportThread } from '../types/support-chat';

/**
 * Cookie storage configuration
 */
export interface CookieStorageConfig {
  /** Cookie name for storing thread list */
  cookieName: string;
  /** Cookie expiration in days (default: 365) */
  maxAge?: number;
  /** Cookie path (default: '/') */
  path?: string;
  /** Whether cookie is secure (HTTPS only, default: true in production) */
  secure?: boolean;
  /** SameSite attribute (default: 'lax') */
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Default cookie configuration
 */
const DEFAULT_COOKIE_CONFIG: Required<Omit<CookieStorageConfig, 'cookieName'>> = {
  maxAge: 365,
  path: '/',
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  sameSite: 'lax',
};

/**
 * Set a cookie value
 */
function setCookie(
  name: string,
  value: string,
  config: Required<Omit<CookieStorageConfig, 'cookieName'>>
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + config.maxAge * 24 * 60 * 60 * 1000);

  const cookieParts = [
    `${name}=${encodeURIComponent(value)}`,
    `expires=${expires.toUTCString()}`,
    `path=${config.path}`,
    `SameSite=${config.sameSite}`,
  ];

  if (config.secure) {
    cookieParts.push('Secure');
  }

  document.cookie = cookieParts.join('; ');
}

/**
 * Get a cookie value
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }

  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name: string, path: string = '/'): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
}

/**
 * Get user's support threads from cookie
 * 
 * @param cookieName - Cookie name for storing threads
 * @returns Array of support threads or empty array if none found
 */
export function getCookieSupportThreads(cookieName: string): SupportThread[] {
  try {
    const cookieValue = getCookie(cookieName);
    if (!cookieValue) {
      return [];
    }

    // Parse JSON array of threads
    const threads = JSON.parse(cookieValue) as SupportThread[];
    return Array.isArray(threads) ? threads : [];
  } catch (error) {
    console.error('Error getting cookie support threads:', error);
    return [];
  }
}

/**
 * Update user's support threads in cookie
 * 
 * @param cookieName - Cookie name for storing threads
 * @param threads - Array of support threads to store
 * @param config - Cookie configuration
 * @returns Success status
 */
export function updateCookieSupportThreads(
  cookieName: string,
  threads: SupportThread[],
  config?: Partial<CookieStorageConfig>
): boolean {
  try {
    const fullConfig: Required<Omit<CookieStorageConfig, 'cookieName'>> = {
      ...DEFAULT_COOKIE_CONFIG,
      ...config,
    };

    const cookieValue = JSON.stringify(threads);
    setCookie(cookieName, cookieValue, fullConfig);
    return true;
  } catch (error) {
    console.error('Error updating cookie support threads:', error);
    return false;
  }
}

/**
 * Add a support thread to cookie storage
 * 
 * @param cookieName - Cookie name for storing threads
 * @param thread - Thread to add
 * @param config - Cookie configuration
 * @returns Success status
 */
export function addCookieSupportThread(
  cookieName: string,
  thread: SupportThread,
  config?: Partial<CookieStorageConfig>
): boolean {
  try {
    const existingThreads = getCookieSupportThreads(cookieName);
    
    // Check if thread already exists
    const existingIndex = existingThreads.findIndex((t) => t.threadId === thread.threadId);
    
    if (existingIndex >= 0) {
      // Update existing thread
      existingThreads[existingIndex] = thread;
    } else {
      // Add new thread
      existingThreads.push(thread);
    }

    return updateCookieSupportThreads(cookieName, existingThreads, config);
  } catch (error) {
    console.error('Error adding cookie support thread:', error);
    return false;
  }
}

/**
 * Remove a support thread from cookie storage
 * 
 * @param cookieName - Cookie name for storing threads
 * @param threadId - Thread ID to remove
 * @param config - Cookie configuration
 * @returns Success status
 */
export function removeCookieSupportThread(
  cookieName: string,
  threadId: string,
  config?: Partial<CookieStorageConfig>
): boolean {
  try {
    const existingThreads = getCookieSupportThreads(cookieName);
    const filteredThreads = existingThreads.filter((t) => t.threadId !== threadId);
    
    return updateCookieSupportThreads(cookieName, filteredThreads, config);
  } catch (error) {
    console.error('Error removing cookie support thread:', error);
    return false;
  }
}

/**
 * Find a support thread by ID in cookie storage
 * 
 * @param cookieName - Cookie name for storing threads
 * @param threadId - Thread ID to find
 * @returns Thread if found, null otherwise
 */
export function findCookieSupportThread(
  cookieName: string,
  threadId: string
): SupportThread | null {
  try {
    const threads = getCookieSupportThreads(cookieName);
    return threads.find((t) => t.threadId === threadId) || null;
  } catch (error) {
    console.error('Error finding cookie support thread:', error);
    return null;
  }
}

